import { useEffect, useRef, useState, type KeyboardEvent, type Ref } from 'react';

/**
 * The one-time password, shown once, held to copy (canvas *Athlete Portal*,
 * row F, boards F4 and F5, approved 26 October; as iOS Contacts does a phone
 * number). Hold it and Copy appears above it; tap Copy and it reads Copied,
 * then goes. A tap anywhere else puts Copy away.
 *
 * The copy is made on the tap of Copy, not when the hold is reached: iPhone
 * Safari copies only inside a tap, never from a timer. The phone's own
 * long-press selection is turned off on the box so the two do not compete.
 * From a keyboard, Enter or Space on the box brings Copy up.
 */

const HOLD_MS = 500;
const COPIED_MS = 1500;

export function OneTimePassword({ value, boxRef }: { value: string; boxRef?: Ref<HTMLDivElement> }) {
  const [bubble, setBubble] = useState<'none' | 'copy' | 'copied'>('none');
  const hold = useRef<number | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const copyButton = useRef<HTMLButtonElement>(null);

  function stopHold() {
    if (hold.current !== null) window.clearTimeout(hold.current);
    hold.current = null;
  }

  useEffect(() => stopHold, []);

  // Copy goes away on a tap anywhere else.
  useEffect(() => {
    if (bubble !== 'copy') return;
    const away = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setBubble('none');
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [bubble]);

  useEffect(() => {
    if (bubble !== 'copied') return;
    const gone = window.setTimeout(() => setBubble('none'), COPIED_MS);
    return () => window.clearTimeout(gone);
  }, [bubble]);

  function open(fromKeyboard: boolean) {
    setBubble('copy');
    if (fromKeyboard) window.setTimeout(() => copyButton.current?.focus(), 0);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Older Safari: the selection copy, still inside this tap.
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      const copied = document.execCommand('copy');
      field.remove();
      if (!copied) {
        setBubble('none');
        return;
      }
    }
    setBubble('copied');
  }

  function key(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(true);
    }
    if (event.key === 'Escape') setBubble('none');
  }

  return (
    <div className="one-time-password-wrap" ref={wrap}>
      {bubble === 'copy' ? (
        <button type="button" className="copy-bubble" ref={copyButton} onClick={() => void copy()}>
          Copy
        </button>
      ) : null}
      {bubble === 'copied' ? (
        <span className="copy-bubble copied" role="status">
          Copied
        </span>
      ) : null}
      <div
        ref={boxRef}
        className="one-time-password"
        data-held={bubble !== 'none' ? '' : undefined}
        tabIndex={0}
        onPointerDown={() => {
          stopHold();
          hold.current = window.setTimeout(() => {
            hold.current = null;
            open(false);
          }, HOLD_MS);
        }}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onContextMenu={(event) => event.preventDefault()}
        onKeyDown={key}
      >
        {value}
      </div>
    </div>
  );
}
